import React, { FC, useEffect, useState } from "react";
import { Button, Col, notification, Row, Spin, Divider, Form } from "antd";
import styled from "styled-components";
import { Text3D } from "../Components/Text3D";
import { useMutation, useLazyQuery } from "@apollo/client";
import { CREATE_ORDER_V2 } from "../Apollo/graphql/Mutation/OrderMutations";
import { CREATE_DESIGN } from "../Apollo/graphql/Mutation/DesignsMutations";
import { uploadtoS3 } from "../Api/fileUpload";
import {
  GENERATE_3DPREVIEW_PRE_SIGNED_URL_MUTATION,
  GENERATE_PRE_SIGNED_URL_MUTATION,
} from "../Apollo/graphql/Mutation/UploadFile";

import { useHistory, NavLink } from "react-router-dom";
import { useStoreState, useStoreActions } from "../myStore";
import { getCookies, getUserData } from "../utils/utilsFunctions";
import HeaderComponent from "../Components/HeaderComponents";
import FooterPublic3D from "../Components/FooterPublic3D";
import { GET_ORDER_PRICE_V2 } from "../Apollo/graphql/Query";
import { replaceError } from "../utils/utilsFunctions";
import { Button3D } from "../Components/Button3D";
import { LocalImage } from "../Components/LocalImage";
import ShippingDetailsModal from "../Components/ShippingDetailsModal";
import EditCartModal from "../Components/EditCartModal";

const MainWrapper = styled(Row)``;

const ContentDiv = styled.div`
  background-color: rgb(247, 247, 247);
  font-family: "Planer", -apple-system, BlinkMacSystemFont, sans-serif;
  width: 100%;
  padding: 2rem 0rem;
`;

const DesignsListSection = styled.div`
  padding: 2rem;
  @media screen and (max-width: 768px) {
    padding: 0rem;
  }
`;

const Section = styled.div`
  padding: 1rem 0rem;
  display: flex;
  flex-direction: column;
`;

const SinglePackageWrapper = styled.div`
  background-color: #ffffff;
  margin-bottom: 12px;
  .printer-owner-wrapper {
    padding: 16px 16px 0px;
  }
  .printer-designs-wrapper {
    padding: 0px 16px 16px;
    .single-item-wrapper {
      margin-bottom: 16px;
    }
  }
`;

const OrderSummarySection = styled.div`
  background-color: #ffffff;
  padding: 0px 16px;
  .order-summary-item {
    display: flex;
    flex: 1;
    justify-content: space-between;
  }
  .order-summary-action-item {
    margin-top: 10px;
  }
`;

const Cart: FC = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const loginUser = !!getCookies() && !!getUserData();
  const [loading, setLoading] = useState(true);
  const [currentProgressTitle, setCurrentProgressTitle] = useState("");
  const [isShippingModalVisible, setIsShippingModalVisible] = useState(false);
  const [initialShipping, setInitialShippingType] = useState("PICKUP");
  const [shippingDetails, setShippingDetails] = useState<any>({
    deliveryType: initialShipping,
  });
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState<any>(null);
  const { packages } = useStoreState((state: any) => state.cart.orderCart);
  const { removeFromCart, editCart } = useStoreActions(
    (actions: any) => actions.cart
  );
  const [uploadPriviewFileMutation] = useMutation(
    GENERATE_3DPREVIEW_PRE_SIGNED_URL_MUTATION
  );
  const [uploadFileMutation] = useMutation(GENERATE_PRE_SIGNED_URL_MUTATION);
  const [createDesignMutation] = useMutation(CREATE_DESIGN);
  const [createOrderMutation] = useMutation(CREATE_ORDER_V2);
  const [
    getOrderPriceV2,
    { loading: orderPriceLoading, data: orderPriceResult },
  ] = useLazyQuery(GET_ORDER_PRICE_V2, {
    onCompleted: () => {
      setLoading(false);
      setCurrentProgressTitle("");
    },
    onError: (err) => {
      notification.error({
        message: "Error",
        description: replaceError(err),
      });
      setLoading(false);
    },
  });

  useEffect(() => {
    if (packages && packages.length > 0) {
      setCurrentProgressTitle("Estimating Price...");
      fetchOrderPrice(packages, shippingDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages]);

  useEffect(() => {
    if (isShippingModalVisible) {
      form.setFieldsValue({
        ...shippingDetails,
        shippingArea: shippingDetails.overseas ? "overseas" : "australia",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShippingModalVisible]);

  // file upload on the conditon of no design id
  const uploadPreviewFile = async (fileList: any) => {
    let file = fileList[0].originFileObj;
    try {
      const {
        data: {
          generate3dPreviewPreSignedURL: { url },
        },
      } = await uploadPriviewFileMutation({
        variables: {
          input: {
            type: "put",
            fileId: file.uid ? file.uid : `${new Date().getTime()}`,
            mime: file.name.includes(".stl") ? "model/stl" : file.type,
          },
        },
      });
      if (url) {
        await uploadtoS3(url, file);
        return url.split("?")[0];
      }
    } catch (err) {
      setLoading(false);
      return false;
    }
  };

  // file uploading
  const uploadFile = async (fileList: any) => {
    setCurrentProgressTitle("Uploading design file...");
    let file = fileList[0].originFileObj;
    try {
      const {
        data: {
          generate3dPreSignedURL: { url },
        },
      } = await uploadFileMutation({
        variables: {
          input: {
            type: "put",
            fileId: file.uid ? file.uid : `${new Date().getTime()}`,
            mime: file.name.includes(".stl") ? "model/stl" : file.type,
          },
        },
      });
      if (url) {
        await uploadtoS3(url, file);
        notification.success({
          message: "Success",
          description: "Design Filed Uploaded",
        });
        return url.split("?")[0];
      }
    } catch (err) {
      setLoading(false);
      return false;
    }
  };

  const getFormattedOrderInput = async (packages, shippingDetails) => {
    let finalOrderPriceInput: any = { packages: [] };
    let updatedCart: any = { packages: [] };
    for (let index = 0; index < packages.length; index++) {
      const singlePackage = packages[index];
      const { items, sellerName, shippingFrom, postalAddress, ...rest } =
        singlePackage;
      let finalItem: any = [];
      let updatedCartItem: any = [];
      for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
        const item = items[itemIndex];
        const {
          files,
          printerName,
          designId,
          designThumbnail,
          designName,
          designUrl,
          uploadType,
          ...rest
        } = item;
        if (!designId && files) {
          const uploadRes = await uploadPreviewFile(files);
          if (!uploadRes) {
            alert("Could not calculate at the moment; design upload error");
          }
          finalItem.push({ ...rest, designUrl: uploadRes });
          updatedCartItem.push({ ...item, designUrl: uploadRes });
        }
        if (designId) {
          finalItem.push({ ...rest, designId });
        }
      }
      finalOrderPriceInput.packages.push({ ...rest, items: finalItem });
      updatedCart.packages.push({ ...singlePackage, items: updatedCartItem });
    }
    if (shippingDetails.deliveryType === "SHIPPING") {
      const { deliveryType, shippingArea, fullname, to, ...rest } =
        shippingDetails;
      const destinationZip =
        shippingArea === "overseas" ? to : to.split("_")[0];
      finalOrderPriceInput["shippingDetail"] = {
        ...rest,
        overseas: shippingArea === "overseas",
        ...(to && { to: destinationZip }),
      };
    }
    return { finalOrderPriceInput, updatedCart };
  };

  const fetchOrderPrice = async (packages, shippingDetails) => {
    const { finalOrderPriceInput } = await getFormattedOrderInput(
      packages,
      shippingDetails
    );
    getOrderPriceV2({
      variables: {
        input: {
          ...finalOrderPriceInput,
        },
      },
    });
  };

  const handleShippingFormSubmit = async (shippingDetails) => {
    setLoading(true);
    setShippingDetails({
      ...shippingDetails,
      overseas:
        shippingDetails.shippingArea &&
        shippingDetails.shippingArea === "overseas",
    });
    setInitialShippingType(shippingDetails.deliveryType.toUpperCase());
    let updatePackages = packages.map((s) => ({
      ...s,
      deliveryType: shippingDetails.deliveryType.toUpperCase(),
    }));
    setIsShippingModalVisible(false);
    fetchOrderPrice(updatePackages, shippingDetails);
  };

  // creating designs
  const uploadDesign = async (designData: any) => {
    let thumbnailImage = null;
    if (designData?.designThumbnail) {
      thumbnailImage = await uploadFile(designData?.designThumbnail);
    }
    setCurrentProgressTitle("Uploading design...");
    try {
      const data = await createDesignMutation({
        variables: {
          input: {
            name: designData.designName,
            description: designData.description || "",
            featuredImage: thumbnailImage,
            summary: designData.summary || "",
            design: designData.design,
            images: designData.images,
            marketPlaceType: designData.uploadType,
            price: designData.designPrice,
          },
        },
      });
      notification.success({
        message: "Success",
        description: "Design created",
      });
      return data.data.createDesign._id;
    } catch (err) {
      setLoading(false);
      notification.error({
        message: "Error",
        description: replaceError(err),
      });
    }
  };

  const handlePlaceOrder = () => {
    if (!loginUser) {
      history.push(`/signin?redirect=${window.location.pathname}`);
      return;
    }
    placeOrder();
  };

  const placeOrder = async () => {
    try {
      setCurrentProgressTitle("Creating order...");
      setLoading(true);
      let finalOrderPriceInput: any = { packages: [] };
      for (let index = 0; index < packages.length; index++) {
        const singlePackage = packages[index];
        const { items, sellerName, shippingFrom, postalAddress, ...rest } =
          singlePackage;
        let finalItem: any = [];
        for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
          const item = items[itemIndex];
          const {
            files,
            printerName,
            designId,
            designThumbnail,
            designName,
            designUrl,
            uploadType,
            ...rest
          } = item;
          if (!designId && files) {
            const uploadRes = await uploadFile(files);
            if (!uploadRes) {
              alert("Could not calculate at the moment; design upload error");
              return;
            }
            const designRes = await uploadDesign({
              ...rest,
              images: [uploadRes],
              design: uploadRes,
              designName,
              designThumbnail,
              uploadType,
            });
            finalItem.push({
              ...rest,
              designId: designRes,
            });
          }
          if (designId) {
            finalItem.push({ ...rest, designId });
          }
        }
        finalOrderPriceInput.packages.push({
          ...rest,
          items: finalItem,
          deliveryType: shippingDetails.deliveryType,
        });
      }
      if (shippingDetails.deliveryType === "SHIPPING") {
        const { deliveryType, shippingArea, fullname, to, ...rest } =
          shippingDetails;
        const destinationZip =
          shippingArea === "overseas" ? to : to.split("_")[0];
        finalOrderPriceInput["shippingDetail"] = {
          ...rest,
          overseas: shippingArea === "overseas",
          ...(to && { to: destinationZip }),
        };
      }
      const {
        data: { createOrderV2 },
      } = await createOrderMutation({
        variables: {
          input: {
            ...finalOrderPriceInput,
          },
        },
      });
      notification.success({
        message: "Success",
        description: "Order Created Successfully",
      });
      window.location.replace(createOrderV2);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const handleEdit = async (packageIdx, itemIdx, item) => {
    setCurrentEditItem({ packageIdx, itemIdx, item });
    setIsEditModalVisible(true);
  };

  const handleEditModalConfirm = async (currentlyActive, changedItem) => {
    const editedItem = { ...currentlyActive.item, ...changedItem };
    editCart({
      packageIndex: currentlyActive.packageIdx,
      itemIndex: currentlyActive.itemIdx,
      item: editedItem,
    });
    setIsEditModalVisible(false);
    setCurrentEditItem(null);
    setCurrentProgressTitle("Wait a moment...");
    setLoading(true);
  };

  return (
    <Spin spinning={loading || orderPriceLoading} tip={currentProgressTitle}>
      <MainWrapper>
        <HeaderComponent />
        <ContentDiv>
          <Col span={20} offset={2}>
            <Row>
              <Col xs={{ span: 24 }} md={{ span: 24 }}>
                <DesignsListSection>
                  <Row gutter={{ xs: 0, md: 24 }}>
                    <Col span={16}>
                      <Section>
                        <Row justify="space-between">
                          <Col>
                            <Text3D textWeight={700} textSize="2rem">
                              Design & Specifications
                            </Text3D>
                          </Col>
                          <Col>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <NavLink to={`/design/upload`}>
                                <Button3D
                                  type="primary"
                                  style={{
                                    width: "100%",
                                    fontSize: "15px",
                                    height: "32px",
                                    fontWeight: 600,
                                  }}
                                >
                                  Add new design
                                </Button3D>
                              </NavLink>
                              <NavLink to={`/marketplace`}>
                                <Button3D
                                  type="primary"
                                  style={{
                                    width: "100%",
                                    fontSize: "15px",
                                    height: "32px",
                                    fontWeight: 600,
                                  }}
                                >
                                  Add from marketplace
                                </Button3D>
                              </NavLink>
                            </div>
                          </Col>
                        </Row>
                      </Section>
                    </Col>
                  </Row>
                  <Row gutter={{ xs: 0, md: 24 }}>
                    <Col xs={{ span: 24 }} md={{ span: 16 }}>
                      {packages && packages.length === 0 && (
                        <Row>
                          <Col>
                            <Section>
                              <Text3D textWeight={700}>
                                No items in the cart
                              </Text3D>
                            </Section>
                          </Col>
                        </Row>
                      )}
                      {packages.map((singlePackage, pdx) => (
                        <Row key={pdx}>
                          <Col span={24}>
                            <SinglePackageWrapper>
                              <div className="printer-owner-wrapper">
                                <div>
                                  <Text3D textSize="1rem" textWeight={600}>
                                    Printer Hub Owner:{" "}
                                    {singlePackage.sellerName}
                                  </Text3D>
                                </div>
                                <div>
                                  <Text3D textSize="1rem" textWeight={600}>
                                    Delivery Type: {singlePackage.deliveryType}
                                  </Text3D>
                                </div>
                                {singlePackage.deliveryType &&
                                  singlePackage.deliveryType === "PICKUP" && (
                                    <div>
                                      <Text3D textSize="1rem" textWeight={600}>
                                        Pickup Location:{" "}
                                        {singlePackage.postalAddress}
                                      </Text3D>
                                    </div>
                                  )}
                              </div>
                              <Divider plain />
                              <div className="printer-designs-wrapper">
                                {singlePackage.items.map((item, idx) => (
                                  <div
                                    className="single-item-wrapper"
                                    key={idx}
                                  >
                                    <Row>
                                      <Col span={8}>
                                        {item.designThumbnail && (
                                          <LocalImage
                                            file={
                                              item.designThumbnail[0][
                                                "originFileObj"
                                              ]
                                            }
                                            style={{
                                              height: "110px",
                                              width: "auto",
                                            }}
                                          />
                                        )}
                                      </Col>
                                      <Col span={8}>
                                        <div>
                                          <div>
                                            <Text3D
                                              textWeight={600}
                                              textSize="1rem"
                                            >
                                              {item.designName}
                                            </Text3D>
                                          </div>
                                          <div>
                                            <span style={{ color: "#757575" }}>
                                              Printer:{" "}
                                            </span>
                                            <NavLink
                                              to={`/printers/${item.printerId}`}
                                            >
                                              {item.printerName}
                                            </NavLink>
                                          </div>
                                        </div>
                                      </Col>
                                      <Col span={6}>
                                        <div>
                                          <div>
                                            <span>Quantity: </span>
                                            <span>{item.quantity}</span>
                                            <span>
                                              <Button
                                                type="link"
                                                onClick={() => {
                                                  handleEdit(pdx, idx, item);
                                                }}
                                              >
                                                Edit
                                              </Button>
                                            </span>
                                          </div>
                                          <div>
                                            <span>Infill: </span>
                                            <span>{item.infill}</span>
                                          </div>
                                          <div>
                                            <span>Scale: </span>
                                            <span>{item.scale}</span>
                                          </div>
                                          <div>
                                            <span>Material: </span>
                                            <span>{item.material}</span>
                                          </div>
                                          <div>
                                            <span>Color: </span>
                                            <span>{item.color}</span>
                                          </div>
                                        </div>
                                      </Col>
                                      <Col span={2}>
                                        <Button
                                          type="link"
                                          onClick={() => {
                                            removeFromCart({
                                              packageIndex: pdx,
                                              itemIndex: idx,
                                            });
                                          }}
                                        >
                                          Remove
                                        </Button>
                                      </Col>
                                    </Row>
                                  </div>
                                ))}
                              </div>
                            </SinglePackageWrapper>
                          </Col>
                        </Row>
                      ))}
                    </Col>
                    <Col xs={{ span: 24 }} md={{ span: 8 }}>
                      <OrderSummarySection>
                        <Row>
                          <Col>
                            <Section>
                              <Text3D textWeight={700}>Order Summary</Text3D>
                            </Section>
                          </Col>
                        </Row>
                        <Row>
                          <Col span={24}>
                            <Section>
                              <div>
                                <div className="order-summary-item">
                                  <Text3D textWeight={600} textSize="1rem">
                                    Total Packages:
                                  </Text3D>
                                  <Text3D textWeight={600} textSize="1rem">
                                    {packages && packages.length}
                                  </Text3D>
                                </div>
                                <div className="order-summary-item">
                                  <Text3D textWeight={600} textSize="1rem">
                                    Delivery Type:
                                  </Text3D>
                                  <div>
                                    <Button
                                      type="link"
                                      onClick={() =>
                                        setIsShippingModalVisible(true)
                                      }
                                    >
                                      Edit
                                    </Button>
                                    <Text3D
                                      textWeight={600}
                                      textSize="1rem"
                                      textTransform="uppercase"
                                    >
                                      {initialShipping}
                                    </Text3D>
                                  </div>
                                </div>
                                {/* {shippingDetails.deliveryType === "SHIPPING" && (
                                  <div className="shipping-details">
                                    {!shippingDetails.shippingArea && <span>Please complete shipping form</span>}
                                  </div>
                                )} */}
                                <div className="order-summary-item">
                                  <Text3D textWeight={600} textSize="1rem">
                                    Shipping:
                                  </Text3D>
                                  <Text3D textWeight={600} textSize="1rem">
                                    {orderPriceResult &&
                                      orderPriceResult.orderPriceV2 &&
                                      orderPriceResult.orderPriceV2
                                        .totalShippingPrice}
                                  </Text3D>
                                </div>
                                <div className="order-summary-item">
                                  <Text3D textWeight={600} textSize="1rem">
                                    Total:
                                  </Text3D>
                                  <Text3D
                                    textWeight={600}
                                    textSize="1rem"
                                    textColor="#1196DD"
                                  >
                                    A${" "}
                                    {orderPriceResult &&
                                      orderPriceResult.orderPriceV2 &&
                                      orderPriceResult.orderPriceV2.grossTotal}
                                  </Text3D>
                                </div>
                              </div>
                              <div>
                                <div className="order-summary-action-item">
                                  <Button3D
                                    type="primary"
                                    style={{
                                      width: "100%",
                                      fontSize: "18px",
                                      height: "46px",
                                      fontWeight: 600,
                                    }}
                                    disabled={
                                      loading ||
                                      orderPriceLoading ||
                                      packages.length === 0
                                    }
                                    onClick={handlePlaceOrder}
                                  >
                                    Place Order
                                  </Button3D>
                                </div>
                              </div>
                            </Section>
                          </Col>
                        </Row>
                      </OrderSummarySection>
                    </Col>
                  </Row>
                </DesignsListSection>
              </Col>
            </Row>
          </Col>
        </ContentDiv>
        {isShippingModalVisible && (
          <ShippingDetailsModal
            isShippingModalVisible={isShippingModalVisible}
            shippingDetails={shippingDetails}
            handleShippingFormSubmit={handleShippingFormSubmit}
            handleClose={() => setIsShippingModalVisible(false)}
            form={form}
            onDeliveryTypeChange={(val) =>
              setShippingDetails({ ...shippingDetails, deliveryType: val })
            }
            onOverseasChange={(val) =>
              setShippingDetails({
                ...shippingDetails,
                overseas: val === "overseas",
              })
            }
          />
        )}
        {currentEditItem && (
          <EditCartModal
            isEditModalVisible={isEditModalVisible}
            handleClose={() => {
              setIsEditModalVisible(false);
              setCurrentEditItem(null);
            }}
            handleConfirm={handleEditModalConfirm}
            currentEditItem={currentEditItem}
          />
        )}
        <FooterPublic3D />
      </MainWrapper>
    </Spin>
  );
};
export default Cart;
